import { ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

import { StoryCard } from "@/components/StoryCard";
import { VerticalVideoFrame } from "@/components/VerticalVideoFrame";
import { Button } from "@/components/ui/button";
import { usePageSeo } from "@/hooks/usePageSeo";
import { ENTITY_PAGE_CONFIG, getEntityStories } from "@/utils/entityPages";

export const EntityLandingPage = ({ entityKey, feed }) => {
  const entity = ENTITY_PAGE_CONFIG[entityKey];
  const stories = getEntityStories(feed, entityKey);
  const featuredStory = stories[0];

  usePageSeo({
    canonicalPath: entity.route,
    description: entity.description,
    title: `${entity.title} | Content Watch HQ`,
  });

  return (
    <main className="page-shell mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid={`entity-page-${entity.key}`}>
      <section className="grid gap-8 border border-[#D9DCE1] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.04)] lg:grid-cols-[minmax(0,1fr)_340px] lg:p-8" data-testid={`entity-page-hero-${entity.key}`}>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]" data-testid={`entity-page-overline-${entity.key}`}>{entity.eyebrow}</p>
          <h1 className="mt-3 font-[Bebas_Neue] text-5xl uppercase tracking-[0.04em] text-[#0A0A0A] sm:text-6xl" data-testid={`entity-page-title-${entity.key}`}>{entity.title}</h1>
          <p className="mt-4 max-w-3xl text-base leading-8 text-slate-700" data-testid={`entity-page-description-${entity.key}`}>{entity.description}</p>
          <p className="mt-5 max-w-2xl border-l-4 border-[#1338BE] pl-4 text-sm font-semibold uppercase tracking-[0.18em] text-[#1338BE]" data-testid={`entity-page-hero-copy-${entity.key}`}>{entity.heroCopy}</p>
          <div className="mt-7 flex flex-wrap gap-2" data-testid={`entity-page-targets-${entity.key}`}>
            {entity.searchTargets.map((target) => (
              <span key={target} className="border border-[#D9DCE1] bg-[#F8F9FA] px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-700" data-testid={`entity-page-target-${entity.key}-${target.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {target}
              </span>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild className="rounded-none bg-[#1338BE] px-5 py-5 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#102F9B]" data-testid={`entity-page-investigations-link-${entity.key}`}>
              <Link to="/investigations">
                Open Investigations Desk
                <ArrowRight size={16} weight="bold" />
              </Link>
            </Button>
          </div>
        </div>
        {featuredStory ? (
          <VerticalVideoFrame compact story={featuredStory} />
        ) : (
          <div className="border border-[#D9DCE1] bg-[#F8F9FA] p-6" data-testid={`entity-page-empty-hero-${entity.key}`}>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]">Entity page live</p>
            <p className="mt-3 text-sm leading-7 text-slate-700">This page is indexed and ready. Matching stories will fill in automatically as the newsroom publishes more entity-focused coverage.</p>
          </div>
        )}
      </section>

      <section className="news-card-grid mt-10" data-testid={`entity-page-grid-${entity.key}`}>
        {stories.length ? stories.map((story) => <StoryCard key={story.id || story.slug} story={story} />) : (
          <div className="border border-[#D9DCE1] bg-white p-8 md:col-span-2 xl:col-span-3" data-testid={`entity-page-empty-state-${entity.key}`}>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]">No stories yet</p>
            <p className="mt-3 text-base leading-8 text-slate-700">The SEO route is ready for search traffic now. As Content Watch publishes matching coverage, those stories will appear here automatically.</p>
          </div>
        )}
      </section>
    </main>
  );
};